import { mapFromAudience, mapFromAudienceLines, mapFromAudiences, mapToAudience } from 'mappings/audienceMapping';
import { Audience } from 'models/audience/audience';
import { AudienceLine } from 'models/audience/audienceLine';
import { AudienceDTO } from 'models/dto/audience/audienceDTO';
import { AudienceLineDTO } from 'models/dto/audience/audienceLineDTO';
import ResourceLinkAudienceDTO from 'models/dto/resourceLinkAudienceDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import AppError from 'utils/appError';
import http from './httpService';

export async function apiGetAudience(
  audienceId: number,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Audience | undefined> {
  try {
    if (!audienceId || audienceId <= 0) return undefined;
    const ar = await http.get<AudienceDTO>(`/audiences/${audienceId}`, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return undefined;

    return mapFromAudience(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetAudienceLines(audienceId: number, accessToken: string): Promise<AudienceLine[]> {
  try {
    const ar = await http.get<AudienceLineDTO[]>(`/audiences/${audienceId}/users`, http.getRequestConfig(accessToken));
    if (!ar || !ar.data) return [];

    return mapFromAudienceLines(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetAudiences(accessToken: string, cache: GlobalDataCache): Promise<Audience[]> {
  try {
    const ar = await http.get<AudienceDTO[]>(`/audiences`, http.getRequestConfig(accessToken));

    return mapFromAudiences(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetAudiencesWithLinks(accessToken: string, cache: GlobalDataCache): Promise<Audience[]> {
  try {
    const ar = await http.get<AudienceDTO[]>(`/audiences/links`, http.getRequestConfig(accessToken));

    return mapFromAudiences(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export const apiAddAudience = async (
  audience: Audience,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Audience | undefined> => {
  try {
    const audienceDTO = mapToAudience(audience);
    const ar = await http.post<AudienceDTO>(`/audiences`, audienceDTO, http.getRequestConfig(accessToken));

    return mapFromAudience(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiUpdateAudience = async (
  audience: Audience,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Audience | undefined> => {
  try {
    const audienceDTO = mapToAudience(audience);
    const ar = await http.put<AudienceDTO>(`/audiences`, audienceDTO, http.getRequestConfig(accessToken));

    return mapFromAudience(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiRemoveAudience = async (id: number, accessToken: string): Promise<void> => {
  try {
    await http.post<AudienceDTO>(`/audiences/delete/${id}`, null, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiSetAudienceLinksForAudience = async (
  audienceId: number,
  dtos: ResourceLinkAudienceDTO[],
  accessToken: string,
): Promise<void> => {
  try {
    await http.post(`audiences/${audienceId}/links`, dtos, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};

export const apiSetAudienceLinksForLink = async (
  linkId: number,
  dtos: ResourceLinkAudienceDTO[],
  accessToken: string,
): Promise<void> => {
  try {
    await http.post(`audiences/links/${linkId}`, dtos, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
};
